/**
 * 랜딩 페이지
 * - 비로그인 사용자에게 보여지는 마케팅 페이지
 * - 주요 기능, 사용 방법, 요금제 미리보기 포함
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';
import ShareButtons from '../components/ui/ShareButtons';
import { useAuthStore } from '../store/authStore';
import { Palette, Package, Cloud, Wand2, Download, Lock, Star } from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  useSEO({ title: 'MD STUDIO', description: '미니디스크 라벨을 쉽고 아름답게 제작하세요.' });

  const features = [
    {
      icon: <Wand2 size={36} className="text-primary-600" />,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
    },
    {
      icon: <Download size={36} className="text-primary-600" />,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
    },
    {
      icon: <Lock size={36} className="text-primary-600" />,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
    },
  ];

  // 아바타 더미 데이터 (실제로는 API에서 가져올 수 있음)
  const avatarColors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const hoverVariants = {
    rest: { y: 0 },
    hover: { y: -8, transition: { duration: 0.3 } },
  };

  const steps = [
    {
      title: t('landing.step1Title'),
      description: t('landing.step1Desc'),
    },
    {
      title: t('landing.step2Title'),
      description: t('landing.step2Desc'),
    },
    {
      title: t('landing.step3Title'),
      description: t('landing.step3Desc'),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* 히어로 섹션 */}
      <section className="relative overflow-hidden bg-brand-hero text-white py-12 sm:py-20 px-4">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[url('/icons/sound-wave.svg')] bg-repeat-x bg-center opacity-20 motion-reduce:opacity-10"
        />
        {/* MD 덱 씬 배경 — xs(380px) 이상에서만 노출, 작은 Fold 커버는 숨김 */}
        <div
          aria-hidden="true"
          className="hidden xs:block absolute inset-0 bg-[url('/illustrations/md-deck-scene.svg')] bg-no-repeat bg-cover bg-center opacity-[0.18] mix-blend-screen motion-reduce:opacity-[0.08] pointer-events-none"
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-2xl xs:text-3xl md:text-5xl lg:text-6xl font-bold font-display mb-6 whitespace-pre-line break-words">
            {t('landing.heroTitle')}
          </h1>
          <p className="text-sm xs:text-base md:text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('landing.heroDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
            {/* Primary CTA — 로그인 상태에 따라 다른 동작 */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 sm:flex-none"
            >
              <Button
                onClick={() => navigate(isAuthenticated ? '/albums' : '/signup')}
                className="bg-white text-primary-700 hover:bg-primary-50 px-8 py-3 text-lg font-semibold rounded-xl h-auto shadow-lg w-full sm:w-auto relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {isAuthenticated ? t('landing.myAlbums') : t('landing.startFree')}
                </span>
                {/* Pulse 애니메이션 배경 */}
                {!isAuthenticated && (
                  <motion.div
                    className="absolute inset-0 bg-white opacity-0"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0, 0.2, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                  />
                )}
              </Button>
            </motion.div>
            {/* Secondary CTA */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 sm:flex-none"
            >
              <Button
                onClick={() => navigate('/gallery')}
                variant="outline"
                className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg rounded-xl h-auto bg-transparent w-full sm:w-auto"
              >
                {t('header.gallery', '갤러리 둘러보기')}
              </Button>
            </motion.div>
            {/* Tertiary — link style (로그인하지 않은 경우만) */}
            {!isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-white hover:underline self-center py-2 min-h-[44px]"
              >
                {t('common.login')}
              </button>
            )}
          </div>
          <p className="text-white/90 text-sm mt-3">{t('landing.freeDesc', '무료로 시작하고, 나만의 미니디스크 라벨을 만들어보세요.')}</p>
        </div>
      </section>

      {/* 소셜 프루프 섹션 */}
      <section className="py-8 sm:py-12 px-4 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border-t border-primary-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {/* 사용자 수 */}
            <div className="text-center">
              <p className="text-3xl sm:text-4xl font-bold text-primary-700">
                2,500<span className="text-lg sm:text-2xl">+</span>
              </p>
              <p className="text-gray-700 dark:text-slate-300 text-sm mt-1">
                {t('landing.socialProof.users')}
              </p>
            </div>

            {/* 구분선 */}
            <div className="hidden sm:block w-px h-16 bg-primary-200" />

            {/* 아바타 스택 + 별점 */}
            <div className="flex flex-col items-center gap-3">
              {/* 겹치는 아바타 5개 */}
              <div className="flex items-center -space-x-3">
                {avatarColors.map((color, i) => (
                  <motion.div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-white shadow-md flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: color }}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.1, duration: 0.4 }}
                    viewport={{ once: true }}
                  >
                    {String.fromCharCode(65 + i)}
                  </motion.div>
                ))}
              </div>
              {/* 별점 */}
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                  {t('landing.socialProof.rating')}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 주요 기능 섹션 */}
      <section className="py-10 sm:py-16 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-slate-100 mb-8 md:mb-12">{t('landing.features')}</h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '0px 0px -100px 0px' }}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.div
                  className="text-center p-6 rounded-2xl bg-gray-50 dark:bg-slate-800 hover:shadow-xl hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50 dark:hover:from-slate-700 dark:hover:to-slate-700 transition-all cursor-pointer h-full"
                  variants={hoverVariants}
                  initial="rest"
                  whileHover="hover"
                >
                  <motion.div
                    className="flex justify-center mb-4"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2 dark:text-slate-100">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-slate-300">{feature.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 사용 방법 섹션 */}
      <section className="relative py-10 sm:py-16 px-4 bg-gray-50 dark:bg-slate-800 overflow-hidden">
        <img
          src="/illustrations/md-player-portable.svg"
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute -right-12 top-1/2 -translate-y-1/2 w-72 h-44 opacity-25 pointer-events-none"
        />
        <div className="relative max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-slate-100 mb-8 md:mb-12">{t('landing.howToUse')}</h2>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div key={step.title} className="flex items-start gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">{step.title}</h3>
                  <p className="text-gray-600 dark:text-slate-300">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 요금제 미리보기 섹션 */}
      <section className="py-10 sm:py-16 px-4 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-slate-100 mb-8 md:mb-12">{t('landing.pricingTitle')}</h2>
          <p className="text-gray-600 dark:text-slate-300 mb-8">{t('landing.pricingDesc')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="p-6 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <h3 className="font-bold text-lg mb-1 dark:text-slate-100">Free</h3>
              <p className="text-3xl font-extrabold mb-2 dark:text-slate-100">₩0</p>
              <p className="text-sm text-gray-700 dark:text-slate-300">월 3회 내보내기</p>
            </div>
            <div className="p-6 rounded-2xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-950 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs px-3 py-1 rounded-full">
                {t('pricing.recommended')}
              </span>
              <h3 className="font-bold text-lg mb-1 dark:text-slate-100">Basic</h3>
              <p className="text-3xl font-extrabold mb-2 dark:text-slate-100">
                ₩3,900<span className="text-sm font-normal text-gray-700 dark:text-slate-300">{t('pricing.perMonth')}</span>
              </p>
              <p className="text-sm text-gray-700 dark:text-slate-300">월 30회 내보내기</p>
            </div>
            <div className="p-6 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <h3 className="font-bold text-lg mb-1 dark:text-slate-100">Premium</h3>
              <p className="text-3xl font-extrabold mb-2 dark:text-slate-100">
                ₩7,900<span className="text-sm font-normal text-gray-700 dark:text-slate-300">{t('pricing.perMonth')}</span>
              </p>
              <p className="text-sm text-gray-700 dark:text-slate-300">{t('pricing.unlimited')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/pricing')}
            variant="outline"
            className="mt-8"
          >
            {t('landing.viewPricing')}
          </Button>
        </div>
      </section>

      {/* 푸터 CTA 섹션 */}
      <section className="py-16 px-4 bg-brand-hero text-white relative overflow-hidden">
        {/* 헤드폰 장식 — md 이상에서만 노출 */}
        <img
          src="/illustrations/headphones.svg"
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute -right-8 -bottom-8 w-48 h-48 opacity-20 pointer-events-none select-none"
        />
        <motion.div
          className="relative z-10 max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12">{t('landing.ctaTitle')}</h2>
          <p className="text-primary-100 mb-8">{t('landing.ctaDesc')}</p>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              onClick={() => navigate(isAuthenticated ? '/albums' : '/signup')}
              className="bg-white text-primary-700 hover:bg-primary-50 px-8 py-3 text-lg font-semibold rounded-xl h-auto relative overflow-hidden group"
            >
              <span className="relative z-10">
                {isAuthenticated ? t('landing.myAlbums') : t('landing.freeSignup')}
              </span>
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* 푸터 */}
      <footer className="py-6 px-4 bg-gray-900 text-gray-400 text-center text-sm">
        <p>{t('landing.copyright', { year: new Date().getFullYear() })}</p>
        <div className="mt-4 flex justify-center">
          <ShareButtons
            url="https://md.endline.co.kr"
            title="MD STUDIO - 미니디스크 라벨 디자이너"
          />
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
